import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeBriefFeedbackLook = 'brieffb-kind-red' | 'brieffb-kind-amber' | 'brieffb-kind-accent' | 'brieffb-change' | 'brieffb-send';

export const isWedgeBriefFeedbackLook = (look: string | undefined): look is WedgeBriefFeedbackLook => look !== undefined && look.startsWith('brieffb-');

/* Released Brief feedback card (How was this brief?): the three kind tiles, the Change link and the Send feedback pill. A kind tile is a
   column of a glyph over a label with a 5px gap, 11px by 4px padding, a 12px radius, 12px bold type and a 1.2 line height; its fill is the
   soft tone, its ink and its 0.5px edge are the tone (red, amber or accent). Change is a bare underlined text button in the inherited ink
   (11.5px semibold). Send is the 8px by 16px pill, 10px radius, 13px bold; at rest it is the label fill with the surface ink, and held
   off (busy) it takes the elevated fill, the tertiary ink and the default cursor. Each keeps the declaration order of the source so the
   shipped ink rules keep matching the serialized text, and the roles keep the legacy variable names. The caller owns the labels, the glyphs
   and the handlers. No look draws a transparent target or an anchor (`wedgeSettingsDrawsHit`): legacy geometry holds and the tiles sit in
   an 8px grid where a grown region would overlap their neighbors. */
export function wedgeBriefFeedbackStyle(look: WedgeBriefFeedbackLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'brieffb-change') return {
    background: 'none', border: 0, color: 'inherit', cursor: 'pointer', fontSize: 11.5, fontWeight: 600, fontFamily: r('font'),
    textDecoration: 'underline', padding: 0,
  };
  if (look === 'brieffb-send') return {
    padding: '8px 16px', borderRadius: 10, background: disabled ? r('bgElev') : r('label'), color: disabled ? r('labelTer') : r('surface'),
    border: 0, cursor: disabled ? 'default' : 'pointer', fontSize: 13, fontWeight: 700, fontFamily: r('font'),
    display: 'inline-flex', alignItems: 'center', gap: 6,
  };
  const tone = look === 'brieffb-kind-red' ? r('red') : look === 'brieffb-kind-amber' ? r('amber') : r('accent');
  const soft = look === 'brieffb-kind-red' ? r('redSoft') : look === 'brieffb-kind-amber' ? r('amberSoft') : r('accentTint');
  return {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, padding: '11px 4px', borderRadius: 12,
    background: soft, color: tone, border: `0.5px solid ${tone}`, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: r('font'),
    lineHeight: 1.2, textWrap: 'balance',
  };
}
